import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  LineChart,
  Line,
} from "recharts";
import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  FONT_UI,
  FONT_HEADING,
  SKIN_THEMES,
} from "../layouts/constants";

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

function getToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("adminToken")
  );
}

function api(path) {
  const base = API_BASE_URL.replace(/\/$/, "");
  if (base.endsWith("/api") && path.startsWith("/api/")) {
    return `${base}${path.replace(/^\/api/, "")}`;
  }
  return `${base}${path}`;
}

function formatValue(value) {
  if (value === null || value === undefined || value === "") return "-";
  if (
    typeof value === "string" &&
    (value.includes("T") || value.includes("Z")) &&
    !Number.isNaN(new Date(value).getTime())
  ) {
    return new Date(value).toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
    });
  }
  return String(value);
}

function normalizeText(value) {
  return String(value || "").toLowerCase();
}

function getRowDate(row) {
  return (
    row.scanning_time ||
    row.created_at ||
    row.start_time ||
    row.end_time ||
    row.updated_at ||
    null
  );
}

function groupCount(rows, key) {
  const map = {};
  rows.forEach((row) => {
    const value = row[key] || "Unknown";
    map[value] = (map[value] || 0) + 1;
  });
  return Object.entries(map)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
}

function groupByDate(rows) {
  const map = {};
  rows.forEach((row) => {
    const rawDate = getRowDate(row);
    if (!rawDate) return;
    const date = new Date(rawDate).toLocaleDateString("en-IN", {
      timeZone: "Asia/Kolkata",
    });
    map[date] = (map[date] || 0) + 1;
  });
  return Object.entries(map).map(([date, count]) => ({
    date,
    count,
  }));
}

function averageNumber(rows, key) {
  const values = rows
    .map((row) => Number(row[key]))
    .filter((value) => !Number.isNaN(value) && value > 0);
  if (!values.length) return 0;
  return Math.round(values.reduce((sum, value) => sum + value, 0) / values.length);
}

function sumNumber(rows, key) {
  return rows.reduce((sum, row) => {
    const value = Number(row[key]);
    return sum + (Number.isNaN(value) ? 0 : value);
  }, 0);
}

function getReportSummary(reportType, rows) {
  const total = rows.length;
  if (reportType === "attendance") {
    return [
      { label: "Total Guards", value: total },
      {
        label: "Active Guards",
        value: rows.filter((r) => normalizeText(r.active_status) === "active").length,
      },
      {
        label: "Approved",
        value: rows.filter((r) => normalizeText(r.approval_status).includes("approve")).length,
      },
      {
        label: "Designations",
        value: new Set(rows.map((r) => r.fld_designation).filter(Boolean)).size,
      },
    ];
  }
  if (reportType === "qr") {
    return [
      { label: "Total QR Scans", value: total },
      { label: "Unique Guards", value: new Set(rows.map((r) => r.guard_id)).size },
      { label: "Routes Covered", value: new Set(rows.map((r) => r.route_id)).size },
      { label: "Checkpoints", value: new Set(rows.map((r) => r.checkpoint_id)).size },
    ];
  }
  if (reportType === "incidents") {
    return [
      { label: "Total Incidents", value: total },
      {
        label: "Open Incidents",
        value: rows.filter((r) => normalizeText(r.status) === "open").length,
      },
      {
        label: "Closed Incidents",
        value: rows.filter((r) => normalizeText(r.status) === "closed").length,
      },
      {
        label: "Avg Resolution Hours",
        value: averageNumber(rows, "resolution_hours"),
      },
    ];
  }
  if (reportType === "alerts") {
    return [
      { label: "Total Alerts", value: total },
      {
        label: "Acknowledged",
        value: rows.filter((r) => r.acknowledged_at).length,
      },
      {
        label: "Pending",
        value: rows.filter((r) => !r.acknowledged_at).length,
      },
      {
        label: "Avg Ack Minutes",
        value: averageNumber(rows, "acknowledgement_minutes"),
      },
    ];
  }
  if (reportType === "route") {
    return [
      { label: "Total Allocations", value: total },
      { label: "Unique Guards", value: new Set(rows.map((r) => r.guard_id)).size },
      { label: "Routes", value: new Set(rows.map((r) => r.route_id)).size },
      {
        label: "Avg Shift Minutes",
        value: averageNumber(rows, "shift_duration_minutes"),
      },
    ];
  }
  if (reportType === "escort") {
    return [
      { label: "Total Escorts", value: total },
      { label: "Unique Guards", value: new Set(rows.map((r) => r.guard_id)).size },
      {
        label: "Total Distance KM",
        value: sumNumber(rows, "distance_km").toFixed(2),
      },
      {
        label: "Avg Duration Minutes",
        value: averageNumber(rows, "duration_minutes"),
      },
    ];
  }
  return [
    { label: "Total Records", value: total },
    { label: "Unique Guards", value: new Set(rows.map((r) => r.guard_id)).size },
    { label: "Report Type", value: reportType.toUpperCase() },
    { label: "Rows", value: total },
  ];
}

const REPORT_TYPES = [
  {
    key: "attendance",
    label: "Attendance",
    endpoint: "/api/sentinel_report/attendance",
  },
  {
    key: "qr",
    label: "QR Scans",
    endpoint: "/api/sentinel_report/qr-scans",
  },
  {
    key: "incidents",
    label: "Incidents",
    endpoint: "/api/sentinel_report/incidents",
  },
  {
    key: "alerts",
    label: "Alerts",
    endpoint: "/api/sentinel_report/alerts",
  },
  {
    key: "route",
    label: "Route Performance",
    endpoint: "/api/sentinel_report/route-performance",
  },
  {
    key: "escort",
    label: "Escort Performance",
    endpoint: "/api/sentinel_report/escort-performance",
  },
];

const CHART_COLORS = [
  "#2563eb",
  "#16a34a",
  "#f97316",
  "#dc2626",
  "#7c3aed",
  "#0891b2",
  "#ca8a04",
  "#475569",
];

export default function SentinelReports() {
  const navigate = useNavigate();
  const skin = useSkin();

  const [guards, setGuards] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageError, setPageError] = useState("");
  const [reportType, setReportType] = useState("attendance");
  const [guardId, setGuardId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  const selectedReport = useMemo(() => {
    return REPORT_TYPES.find((r) => r.key === reportType) || REPORT_TYPES[0];
  }, [reportType]);

  const summaryCards = useMemo(() => {
    return getReportSummary(reportType, rows);
  }, [reportType, rows]);

  const trendData = useMemo(() => {
    return groupByDate(rows);
  }, [rows]);

  const guardChartData = useMemo(() => {
    return groupCount(rows, "guard_name").slice(0, 10);
  }, [rows]);

  const severityChartData = useMemo(() => {
    return groupCount(rows, "severity");
  }, [rows]);

  const statusChartData = useMemo(() => {
    if (reportType === "alerts") return groupCount(rows, "alert_status");
    return groupCount(rows, "status");
  }, [rows, reportType]);

  const routeChartData = useMemo(() => {
    return groupCount(rows, "route_name").slice(0, 10);
  }, [rows]);

  useEffect(() => {
    const token = getToken();
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    if (!token || !isLoggedIn) {
      navigate("/");
      return;
    }
    fetchDashboardData(token);
    fetchGuards(token);
  }, []);

  useEffect(() => {
    fetchReport();
  }, [reportType]);

  const fetchDashboardData = async (token) => {
    try {
      const res = await fetch(`${API_BASE_URL}/dashboard/admin/dashboard`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.ok) {
        const data = await res.json();
        const companyDetails = data?.data?.companyDetails || {};
        setDashboardData(companyDetails);
      }
    } catch (error) {
      console.error("Dashboard fetch error:", error);
    }
  };

  const fetchGuards = async (token) => {
    try {
      const token = getToken();
      const res = await fetch(api("/api/sentinel_report/guards"), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const json = await res.json();
      if (!res.ok || json.success === false) {
        throw new Error(json.message || "Failed to fetch guards");
      }
      setGuards(json.data || []);
    } catch (err) {
      console.error("Guard fetch error:", err);
      toast.error("Failed to load guards");
      setGuards([]);
    }
  };

  const fetchReport = async () => {
    try {
      setLoading(true);
      setPageError("");
      const token = getToken();

      const params = new URLSearchParams();
      if (guardId) params.set("guardId", guardId);
      if (fromDate) params.set("fromDate", fromDate);
      if (toDate) params.set("toDate", toDate);

      const url = api(`${selectedReport.endpoint}?${params.toString()}`);
      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const json = await res.json();
      if (!res.ok || json.success === false) {
        throw new Error(json.message || "Failed to fetch report");
      }

      setRows(json.data || []);
      toast.success(`Report loaded: ${json.data?.length || 0} records`);
    } catch (err) {
      console.error("Report fetch error:", err);
      setRows([]);
      setPageError(err.message || "Failed to fetch report");
      toast.error(err.message || "Failed to fetch report");
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    fetchReport();
  };

  const handleExportCsv = () => {
    if (!rows.length) {
      toast.error("No data to export");
      return;
    }

    try {
      const headers = Object.keys(rows[0]);
      const csv = [
        headers.join(","),
        ...rows.map((row) =>
          headers
            .map((key) => {
              const value = row[key] === null || row[key] === undefined ? "" : String(row[key]);
              return `"${value.replace(/"/g, '""')}"`;
            })
            .join(",")
        ),
      ].join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${selectedReport.key}-report-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`CSV exported successfully! (${rows.length} records)`);
    } catch (err) {
      console.error("CSV export error:", err);
      toast.error("Failed to export CSV");
    }
  };

  const routePage = useMemo(
    () => (dashboardData.user_map === "google" ? "/route-master" : "/route-master1"),
    [dashboardData.user_map]
  );

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="reports"
      activeMenu="reports"
    >
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: {
            background: "var(--card-bg)",
            color: "var(--text-primary)",
            border: "1px solid var(--card-border)",
            borderRadius: "12px",
            padding: "16px 20px",
            fontSize: "13px",
            fontWeight: "600",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          },
          success: {
            iconTheme: {
              primary: "#22c55e",
              secondary: "white",
            },
          },
          error: {
            iconTheme: {
              primary: "#ef4444",
              secondary: "white",
            },
          },
          info: {
            iconTheme: {
              primary: "#3b82f6",
              secondary: "white",
            },
          },
        }}
      />

      <div className="font-['Inter',system-ui,sans-serif] text-[var(--text-primary)]">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-[22px]">
          <div>
            <h1 className="text-[22px] font-extrabold text-[var(--text-primary)] tracking-tight m-0">
              Sentinel Reports
            </h1>
            <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
              Customer-friendly reports with KPI cards, charts, trends and detailed data.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-90 hover:-translate-y-0.5"
          >
            Dashboard
          </button>
        </div>

        {pageError && (
          <div className="mb-4 px-4 py-3 rounded-xl text-[13px] font-bold border bg-[var(--red-faint)] border-[var(--red-border)] text-[var(--red)]">
            {pageError}
          </div>
        )}

        {/* Filters Card */}
        <div className="relative overflow-hidden bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-[var(--accent)]" />
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                Report Type
              </label>
              <select
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
                className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
              >
                {REPORT_TYPES.map((r) => (
                  <option key={r.key} value={r.key}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                Guard
              </label>
              <select
                value={guardId}
                onChange={(e) => setGuardId(e.target.value)}
                className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
              >
                <option value="">All Guards</option>
                {guards.map((g) => {
                  const id = g.fld_memberId || g.member_id || g.guard_id;
                  const name = g.fld_name || g.fullName || g.guard_name || g.fld_username;
                  return (
                    <option key={id} value={id}>
                      {name} ({id})
                    </option>
                  );
                })}
              </select>
            </div>

            <div>
              <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                From Date
              </label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
              />
            </div>

            <div>
              <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                To Date
              </label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
              />
            </div>

            <div className="flex items-end gap-2">
              <button
                type="button"
                onClick={handleApplyFilters}
                className="flex-1 border-0 rounded-xl px-4 py-3 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5"
                style={{
                  background: "var(--btn-bg)",
                  color: "var(--btn-text)",
                  boxShadow: "0 4px 18px var(--accent-glow)",
                }}
              >
                Search
              </button>
              <button
                type="button"
                onClick={handleExportCsv}
                className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-3 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-90 hover:-translate-y-0.5"
              >
                Export
              </button>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-[18px]">
          {summaryCards.map((card) => (
            <div
              key={card.label}
              className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-4 py-4 shadow-[0_8px_32px_rgba(0,0,0,0.3)]"
            >
              <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--text-muted)] m-0">
                {card.label}
              </p>
              <h2 className="text-[26px] font-extrabold text-[var(--accent)] mt-1 m-0">
                {card.value}
              </h2>
            </div>
          ))}
        </div>

        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-6 py-5 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
            <h3 className="text-[17px] font-extrabold text-[var(--text-primary)] m-0">
              Daily Report Trend
            </h3>
            <p className="text-[12px] text-[var(--text-muted)] mb-4 mt-1">
              Shows how report activity changes day by day.
            </p>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={[...trendData].sort((a, b) => {
                    const [d1, m1, y1] = a.date.split("/");
                    const [d2, m2, y2] = b.date.split("/");
                    return new Date(y1, m1 - 1, d1) - new Date(y2, m2 - 1, d2);
                  })}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--divider)" />
                  <XAxis
                    dataKey="date"
                    tick={{ fill: "var(--text-muted)", fontSize: 11 }}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fill: "var(--text-muted)", fontSize: 11 }}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "var(--card-bg)",
                      border: "1px solid var(--card-border)",
                      borderRadius: "8px",
                      color: "var(--text-primary)",
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="#2563eb"
                    strokeWidth={3}
                    dot={{ fill: "#2563eb", strokeWidth: 0 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-6 py-5 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
            <h3 className="text-[17px] font-extrabold text-[var(--text-primary)] m-0">
              Top Guards by Activity
            </h3>
            <p className="text-[12px] text-[var(--text-muted)] mb-4 mt-1">
              Helps customers understand which guards have the highest activity.
            </p>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={guardChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--divider)" vertical={false} />
                  <XAxis
                    dataKey="name"
                    hide
                    tick={{ fill: "var(--text-muted)", fontSize: 11 }}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fill: "var(--text-muted)", fontSize: 11 }}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "var(--card-bg)",
                      border: "1px solid var(--card-border)",
                      borderRadius: "8px",
                      color: "var(--text-primary)",
                    }}
                  />
                  <Bar dataKey="value" fill="#2563eb" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Charts Row 2 - Severity & Status */}
        {(reportType === "alerts" || reportType === "incidents") && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-6 py-5 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
              <h3 className="text-[17px] font-extrabold text-[var(--text-primary)] m-0">
                Severity Breakdown
              </h3>
              <p className="text-[12px] text-[var(--text-muted)] mb-4 mt-1">
                Shows risk level distribution for customer review.
              </p>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={severityChartData}
                      dataKey="value"
                      nameKey="name"
                      outerRadius={95}
                      label={({ name, percent }) =>
                        `${name} ${(percent * 100).toFixed(0)}%`
                      }
                      labelLine={false}
                    >
                      {severityChartData.map((entry, index) => (
                        <Cell
                          key={entry.name}
                          fill={CHART_COLORS[index % CHART_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        background: "var(--card-bg)",
                        border: "1px solid var(--card-border)",
                        borderRadius: "8px",
                        color: "var(--text-primary)",
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-6 py-5 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
              <h3 className="text-[17px] font-extrabold text-[var(--text-primary)] m-0">
                Status Breakdown
              </h3>
              <p className="text-[12px] text-[var(--text-muted)] mb-4 mt-1">
                Shows open, closed, pending, or acknowledged status.
              </p>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusChartData}
                      dataKey="value"
                      nameKey="name"
                      outerRadius={95}
                      label={({ name, percent }) =>
                        `${name} ${(percent * 100).toFixed(0)}%`
                      }
                      labelLine={false}
                    >
                      {statusChartData.map((entry, index) => (
                        <Cell
                          key={entry.name}
                          fill={CHART_COLORS[index % CHART_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        background: "var(--card-bg)",
                        border: "1px solid var(--card-border)",
                        borderRadius: "8px",
                        color: "var(--text-primary)",
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {/* Route Activity Chart */}
        {(reportType === "qr" || reportType === "route") && (
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-6 py-5 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-4">
            <h3 className="text-[17px] font-extrabold text-[var(--text-primary)] m-0">
              Route Activity
            </h3>
            <p className="text-[12px] text-[var(--text-muted)] mb-4 mt-1">
              Shows which routes have the highest report activity.
            </p>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={routeChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--divider)" vertical={false} />
                  <XAxis
                    dataKey="name"
                    tick={{ fill: "var(--text-muted)", fontSize: 11 }}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fill: "var(--text-muted)", fontSize: 11 }}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "var(--card-bg)",
                      border: "1px solid var(--card-border)",
                      borderRadius: "8px",
                      color: "var(--text-primary)",
                    }}
                  />
                  <Bar dataKey="value" fill="#16a34a" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Data Table */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
          <div className="px-6 py-4 border-b border-[var(--divider)] flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h3 className="text-[17px] font-extrabold text-[var(--text-primary)] m-0">
                {selectedReport.label} Detailed Report
              </h3>
              <p className="text-[12px] text-[var(--text-muted)] mt-1">
                Complete data table for customer audit and export.
              </p>
            </div>
            <span className="text-[13px] text-[var(--text-muted)]">
              {rows.length} records
            </span>
          </div>

          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12 gap-3">
                <div className="w-8 h-8 border-3 border-[var(--accent-faint)] border-t-[var(--accent)] rounded-full animate-spin" />
                <span className="text-[var(--text-muted)] text-[13px]">Loading report...</span>
              </div>
            ) : rows.length === 0 ? (
              <div className="text-center py-12 text-[var(--text-muted)] text-[13px]">
                No data found
              </div>
            ) : (
              <table className="w-full text-[13px] font-['Inter',system-ui,sans-serif]">
                <thead>
                  <tr style={{ background: "var(--btn-bg)" }}>
                    {Object.keys(rows[0]).map((key) => (
                      <th
                        key={key}
                        className="px-4 py-3 text-left text-[11px] font-extrabold uppercase tracking-wider text-white whitespace-nowrap"
                      >
                        {key.replace(/_/g, " ").toUpperCase()}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr
                      key={index}
                      className={`border-b border-[var(--divider)] hover:bg-[var(--accent-faint)] transition-colors ${
                        index % 2 === 0 ? "bg-[var(--card-bg)]" : "bg-[var(--surface)]"
                      }`}
                    >
                      {Object.keys(rows[0]).map((key) => (
                        <td
                          key={key}
                          className="px-4 py-3 text-[13px] text-[var(--text-primary)] whitespace-nowrap"
                        >
                          {formatValue(row[key])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}